import React from "react";
import { Typography, TextField } from "@mui/material";
import api from "../../Auth/Api";
import globals from "../../Globals";
import { navigate } from "../../Layout/Router";
import { FormDialog } from "../../Layout/Common";

// Shared bits of the Community page (Docs/Social.md §10, package social M7): the generic social layer of an ISocial title, one
// sub-page per backend service - Guilds (`guilds`), Chat reports (`chat`), Player (`friends`, plus `push` where the title has it).
// Every call is an admin group of the title (`api.group(<service>)`): `#<role>` reads, `<role>` acts.

export const guilds = (description, parts, options) => api.group("guilds")(description, parts, options);
export const chat = (description, parts, options) => api.group("chat")(description, parts, options);
export const friends = (description, parts, options) => api.group("friends")(description, parts, options);
export const push = (description, parts, options) => api.group("push")(description, parts, options);
// Social round 2 (Docs/Social.md §14.8, Docs/Market.md §10): the Profiles and Market pages - `#profile` / `#market` read, `profile` /
// `market` act. `social` = Evil Tower's own group: its `social private <player> on|off` is the one admin verb that writes the privacy flag.
export const profile = (description, parts, options) => api.group("profile")(description, parts, options);
export const market = (description, parts, options) => api.group("market")(description, parts, options);
export const social = (description, parts, options) => api.group("social")(description, parts, options);
// The guild layer + map war (Docs/Guild.md §10, Evil Tower): `#war` reads, `war` acts
export const war = (description, parts, options) => api.group("war")(description, parts, options);

// The branch every sub-page starts on: the last one picked here, else Development (the social layer's test bed)
const BRANCH_KEY = "community.branch";
export const savedBranch = () => { try { return localStorage.getItem(BRANCH_KEY) || "Development"; } catch { return "Development"; } };
export const saveBranch = (branch) => { try { localStorage.setItem(BRANCH_KEY, branch); } catch { } };

// The selected title's roles (array or object-map, as PageLayout reads them); a master holds every role
const titleRoles = () => {
    const raw = globals.loginData?.titles?.roles?.[globals.selectedTitle];
    return Array.isArray(raw) ? raw : raw && typeof raw === "object" ? Object.keys(raw) : [];
};
export const canEdit = (role) => !!globals.loginData?.titles?.isMaster || titleRoles().includes(role);
export const canView = (role) => canEdit(role) || titleRoles().includes(`#${role}`);
export const titleHas = (service) => (globals.loginData?.titles?.titles?.[globals.selectedTitle]?.services ?? []).includes(service);

// A report's / line's channel document id "<title>:<branch>:<channel>" -> the plain channel the verbs take (dm:<a>:<b>, guild:<id>)
export const plainChannel = (id) => {
    const parts = String(id ?? "").split(":");
    return parts.length > 2 && !["dm", "guild", "public"].includes(parts[0]) ? parts.slice(2).join(":") : String(id ?? "");
};

export const date = (v) => (v ? new Date(v).toLocaleString() : "—");
export const JOIN_MODES = ["Open", "Request", "Invite only"];
export const ROLES = ["member", "officer", "leader"];
export const roleName = (r) => (typeof r === "number" ? ROLES[r] ?? String(r) : r ?? "");
export const REPORT_REASONS = { 1: "spam", 2: "abuse", 3: "other" };
export const COSMETIC_KINDS = ["picture", "border", "badge", "title"];
export const num = (v) => (v ?? 0).toLocaleString();

// An account id as a short link to the Players page (the full id in the tooltip)
export function PlayerLink({ id, name }) {
    if (!id) return <span>—</span>;
    return (
        <Typography component="span" variant="body2" title={id} sx={{ color: "primary.main", cursor: "pointer", wordBreak: "break-word" }}
                    onClick={() => navigate({ title: globals.selectedTitle, page: "home", sub: "players", deep: [id] })}>
            {name ? `${name} ` : ""}<span style={{ fontFamily: "monospace" }}>{id.slice(-8)}</span>
        </Typography>
    );
}

// An account id as a short link to its market view (Market → Player)
export function MarketLink({ id, name }) {
    if (!id) return <span>—</span>;
    return (
        <Typography component="span" variant="body2" title={`${id} (its market)`} sx={{ color: "primary.main", cursor: "pointer", wordBreak: "break-word" }}
                    onClick={() => navigate({ title: globals.selectedTitle, page: "market", sub: "player", deep: [id] })}>
            {name ? `${name} ` : ""}<span style={{ fontFamily: "monospace" }}>{id.slice(-8)}</span>
        </Typography>
    );
}

// Every edit verb goes through one of these: what it does in words, an optional input, Confirm.
// `action` = { title, text, input: { label, initial, multiline } | null, confirmText, run(value) -> Promise }
export class ConfirmAction extends React.Component {
    state = { value: "" };

    componentDidUpdate(prev) {
        if (this.props.action && this.props.action !== prev.action) this.setState({ value: this.props.action.input?.initial ?? "" });
    }

    render() {
        const { action, busy, onClose } = this.props;
        const input = action?.input;
        return (
            <FormDialog open={!!action} title={action?.title} onClose={onClose} maxWidth="sm" fullWidth
                        confirmText={action?.confirmText ?? "Confirm"} confirmDisabled={busy || (input && !input.optional && !this.state.value.trim())}
                        onConfirm={() => action.run(this.state.value.trim())}>
                <Typography variant="body2" sx={{ mb: input ? 2 : 0 }}>{action?.text}</Typography>
                {input && <TextField fullWidth size="small" label={input.label} value={this.state.value} multiline={!!input.multiline} minRows={input.multiline ? 4 : undefined}
                                     onChange={e => this.setState({ value: e.target.value })} autoFocus />}
            </FormDialog>
        );
    }
}
